import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import ModalHeaderTitle from './ModalHeaderTitle';
import { useLazyQuery } from '@apollo/client/react';
import { GET_TMS_DUPLICATES } from '../../graphql/client/queries';
import { GetTmsDuplicates, GetTmsDuplicatesVariables } from '../../types/schemaTypes';
import Image from 'react-bootstrap/Image';
import { CustomButton } from '../_common';
import { Textfield } from '../_common/Textfield/Textfield';
import { useStoreContext } from '../App/StoreProvider';
import * as Actions from '../../store/actions/addLoadModal';
import './InitialModal.scss';

import ExcelIcon from '../../assets/file-green-24px.svg';
import UploadCheck from '../../assets/check-green-24px.svg';
import UploadCancel from '../../assets/cancel-red-24px.svg';

import ExcelJS from 'exceljs';
import { Row } from 'react-bootstrap';
import { Col } from 'react-bootstrap';

interface InitialModalProps {
    onClickShow: () => void;
    onClickNext: () => void;
    onClickHide: () => void;
    show: boolean;
    proppantName: string | undefined;
    lofa: string | undefined;
    purchaseOrder: string | undefined;
    wellhead: string | undefined;
    customerId: number;
    loadsRemaining: number;
}

const InitialModal = ({
    onClickNext,
    onClickHide,
    show,
    proppantName,
    lofa,
    purchaseOrder,
    wellhead,
    customerId,
    loadsRemaining,
}: InitialModalProps): React.ReactElement => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [fileObj, setFileObj] = useState<any>('');
    const [continueEnabled, setContinueEnabled] = useState(false);
    const [fileUploaded, setFileUploaded] = useState(false);
    const [textLoadCount, setTextLoadCount] = useState('');
    const [fileException, setFileException] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [duplicatErrorMessage, setDuplicateErrorMessage] = useState('');
    const [tmsIds, setTmsIds] = useState<number[]>([]);
    const [, dispatch] = useStoreContext();
    const [fileUploadRemainingLoadsErrorMessage, setFileUploadRemainingLoadsErrorMessage] = useState('');
    const [getDuplicates, { data: countDuplicates, loading: countDuplicatesLoading }] = useLazyQuery<GetTmsDuplicates, GetTmsDuplicatesVariables>(
        GET_TMS_DUPLICATES,
        { fetchPolicy: 'network-only' },
    );
    const handleCancelClick = (): any => {
        setFileObj('');
        setFileUploaded(false);
        setContinueEnabled(false);
        setTextLoadCount('');
        setFileException('');
        setDuplicateErrorMessage('');
        setFileUploadRemainingLoadsErrorMessage('');
        dispatch(Actions.setNumberOfLoadsToCreate(0));
        dispatch(Actions.setTmsIds([]));
    };

    const handleCancel = (): any => {
        setFileObj('');
        setFileUploaded(false);
        setContinueEnabled(false);
        setTextLoadCount('');
        setFileException('');
        setDuplicateErrorMessage('');
        setFileUploadRemainingLoadsErrorMessage('');
        onClickHide();
    };
    const handleLoadTextChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): void => {
        const countLoads = parseInt(event.target.value) || 0;

        setTextLoadCount(countLoads == 0 ? '' : countLoads.toString());

        if (countLoads < 1) {
            setContinueEnabled(false);
            dispatch(Actions.setNumberOfLoadsToCreate(0));
        } else if (countLoads > loadsRemaining) {
            setContinueEnabled(false);
            dispatch(Actions.setNumberOfLoadsToCreate(0));
            setErrorMessage(`There are only ${loadsRemaining} load(s) left on this Sand PO`);
        } else {
            dispatch(Actions.setNumberOfLoadsToCreate(parseInt(event.target.value)));
            setContinueEnabled(true);
            setErrorMessage('');
        }
    };

    const handleLoadFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (!event || !event.target || !event.target.files) return;
        const xls = event.target.files[0];
        setFileObj(xls);
        setFileException('');
    };

    useEffect(() => {
        if (!countDuplicatesLoading) {
            if (countDuplicates && countDuplicates.getTmsDuplicates && tmsIds.length > 0) {
                if ((countDuplicates.getTmsDuplicates?.duplicateTmsIds ?? []).length === 0) {
                    setFileUploaded(true);
                    setContinueEnabled(true);
                    dispatch(Actions.setNumberOfLoadsToCreate(tmsIds.length));
                    dispatch(Actions.setTmsIds(tmsIds));
                } else {
                    setFileException('Error');
                    setDuplicateErrorMessage('Upload unsuccessful due to duplicate TMS Ids');
                    setFileUploaded(true);
                    setContinueEnabled(false);
                }
            }
        }
    }, [countDuplicates]);

    const checkForTmsDuplicates = (ids: number[]): void => {
        setTmsIds(ids);
        getDuplicates({ variables: { input: { tmsIds: ids } } });
    };

    const fileUpload = async (): Promise<void> => {
        const file = fileObj;
        try {
            const reader = new FileReader();
            reader.onloadend = async () => {
                const buffer = reader.result as ArrayBuffer;
                const workbook = new ExcelJS.Workbook();
                await workbook.xlsx.load(buffer);

                const worksheet = workbook.worksheets[0];
                if (!worksheet) {
                    setFileException('File upload unsuccessful, please check the file and try again!');
                    return;
                }

                const headerRow = worksheet.getRow(1);
                const headers = (headerRow.values as (string | number | null | undefined)[])?.slice(1) ?? [];

                const jsonValues: number[] = [];
                worksheet.eachRow((row, rowNumber) => {
                    if (rowNumber === 1) return;
                    const obj: Record<string, string | number | null | undefined> = {};
                    const values = (row.values as (string | number | null | undefined)[]) ?? [];
                    headers.forEach((h, i) => {
                        if (h != null && String(h).trim()) obj[String(h)] = values[i + 1];
                    });
                    const val = obj['TMS ids'];
                    if (val !== undefined && val !== null && val !== '') {
                        jsonValues.push(Number(val));
                    }
                });

                const uniqueValues = jsonValues.filter((val, id, array) => array.indexOf(val) === id);

                if (jsonValues.length > loadsRemaining) {
                    setFileUploadRemainingLoadsErrorMessage(
                        `There are only ${loadsRemaining} load(s) left on this Sand PO.`,
                    );
                    setFileUploaded(true);
                    setFileException('Error');
                } else if (jsonValues.length !== uniqueValues.length) {
                    setFileException('Error');
                    setDuplicateErrorMessage('Proposed TMS IDs should be unique.');
                    setFileUploaded(true);
                } else {
                    checkForTmsDuplicates(jsonValues);
                }
            };
            reader.readAsArrayBuffer(file);
        } catch (e) {
            setFileException('File upload unsuccessful, please check the file and try again!');
        }
    };
    const uploadButtonDisabled = (): boolean => {
        const Extension = '.xlsx';

        if (!fileObj) return true;

        if (fileObj.name.length > Extension.length && fileObj.name.toLowerCase().endsWith(Extension)) {
            return false;
        }

        return true;
    };

    return (
        <Modal show={show} onHide={handleCancel} size="lg" backdrop="static">
            <Modal.Header className="InitialModal-Header">
                <Modal.Title className="InitialModal-Header-Container">
                    <ModalHeaderTitle
                        title="Add Loads"
                        onCancelClick={handleCancel}
                        proppantName={proppantName}
                        lofa={lofa}
                        jobName={wellhead}
                        load={'0'}
                        sandPO={purchaseOrder}
                    ></ModalHeaderTitle>
                </Modal.Title>
            </Modal.Header>
            <div className="InitialModal-Text">
                Enter the number of loads requested to create loads or upload file to import your information.
            </div>
            <Modal.Body>
                <div className="InitialModal-Body-Container">
                    <div className="InitialModal-Input-Container">
                        {/* Left Side */}
                        <Textfield
                            label="Number of Loads"
                            name="NUMBER OF LOADS"
                            value={textLoadCount}
                            onChange={handleLoadTextChange}
                            onBlur={(): void => {}}
                            error={errorMessage != ''}
                            helperText={errorMessage}
                        />
                    </div>
                    {/* Right Side */}
                    {(customerId === 6 || customerId === 46 || customerId === 85) && (
                        <div
                            className={`InitialModal-FileBrowser-Container InitialModal-FileBrowser-Container-Visible`}
                        >
                            {/* After Upload  */}
                            {fileUploaded ? (
                                <div
                                    className={`Initial-Modal-FileBrowser-Container-Upload-After Initial-Modal-FileBrowser-Container-Upload-${
                                        fileUploaded ? 'Visible' : 'Hidden'
                                    }`}
                                >
                                    <Row>
                                        {fileException === '' ? (
                                            <Col>
                                                <div className="InitialModal-FileBrowser-Text">
                                                    <Image src={ExcelIcon} className="InitialModal-FileBrowser-Image" />
                                                    {fileObj ? fileObj.name : ' '} {'  '}
                                                    {fileObj ? fileObj.size : ' '}
                                                    <div className="File-Successful">
                                                        <Image
                                                            src={UploadCheck}
                                                            className="InitialModal-FileBrowser-Image"
                                                        />
                                                        {'  '}
                                                        File Uploaded Successfully
                                                    </div>
                                                </div>{' '}
                                            </Col>
                                        ) : (
                                            <Col>
                                                <div className="InitialModal-FileBrowser-Text InitialModal-FileBrowser-Text-Middle">
                                                    {duplicatErrorMessage !== ''
                                                        ? duplicatErrorMessage
                                                        : fileUploadRemainingLoadsErrorMessage !== ''
                                                        ? fileUploadRemainingLoadsErrorMessage
                                                        : fileException}
                                                    <Image
                                                        src={UploadCancel}
                                                        onClick={handleCancelClick}
                                                        className="InitialModal-FileBrowser-Image InitialModal-FileBrowser-Image-Cancel"
                                                    />
                                                </div>
                                            </Col>
                                        )}
                                    </Row>
                                </div>
                            ) : (
                                <div
                                    className={`Initial-Modal-FileBrowser-Container-Upload-Before  Initial-Modal-FileBrowser-Container-Upload-${
                                        fileUploaded ? 'Hidden' : 'Visible'
                                    }`}
                                >
                                    <div className="Row InitialModal-FileBrowser-Background">
                                        <label className="InitialModal-FileBrowser-Label">
                                            {fileObj ? fileObj.name : 'Click here to choose file'}
                                            <input
                                                type="file"
                                                onChange={e => handleLoadFileChange(e)}
                                                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                                className="InitialModal-FileBrowser-Input"
                                            />
                                        </label>
                                        <button
                                            disabled={uploadButtonDisabled()}
                                            onClick={fileUpload}
                                            className={`InitialModal-Upload-Button`}
                                        >
                                            Upload
                                        </button>
                                    </div>
                                    <div className="Row">
                                        <div className="InitialModal-FileBrowser-Text">Excel (.XSLX) format only</div>
                                    </div>
                                </div>
                            )}

                            {/* Before Upload  */}
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="InitialModal-Footer">
                <CustomButton title="Cancel" isBordered onClick={handleCancel}></CustomButton>
                <CustomButton isDisabled={!continueEnabled} title="Continue" onClick={onClickNext}></CustomButton>
            </Modal.Footer>
        </Modal>
    );
};

export default InitialModal;
